<script setup lang="ts">
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import {ref, reactive, computed, onMounted, watch, nextTick} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {loadCachedPayerDirectory} from '@/ts/composables/stateful/use-insurance-payer-directory';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useI18n} from 'vue-i18n';
import {useQuasar} from 'quasar';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import type {InsuranceVerificationRequest, InsuranceVerificationResponse, PayerDto} from '@/ts/types/dto/insurance-dto';

type DialogState = 'FORM' | 'VERIFYING' | 'SUCCESS' | 'ACCEPTING' | 'ERROR';

const props = defineProps<{
  // The intervention this verification is run for; associates the stored verification (CDC DPP).
  interventionId?: string;
}>();

const emit = defineEmits<{
  // Coverage was verified, accepted by the user, and the insurance-backed license was granted.
  funded: [result: InsuranceVerificationResponse];
  skipped: [];
  // User backed out (the header X) — return to the eligibility decision modal (the 3 options).
  back: [];
}>();

const api = useApi();
const logger = useLogger('InsuranceVerificationDialog');
const {t} = useI18n();
const $q = useQuasar();
const showModal = defineModel<boolean>({required: true});

const state = ref<DialogState>('FORM');
const payers = ref<PayerDto[]>([]);
const payerOptions = ref<PayerDto[]>([]);
const result = ref<InsuranceVerificationResponse | null>(null);
const errorMessage = ref<string | null>(null);
const errorTitle = ref('');
// True for genuine technical failures (vendor down / timeout / unexpected) → red error icon. False
// for user-fixable results (coverage not found, invalid member id) → calm info icon, same severity
// as an inactive plan or an insurer without online verification (the user can fix details and retry).
const errorTechnical = ref(true);
// Which step produced the current ERROR, so Retry can re-attempt the accept (the verification is
// still valid — re-verifying would be a second billable vendor transaction) instead of resetting
// to the form.
const errorStage = ref<'verify' | 'accept'>('verify');
const isLoadingPayers = ref(false);
const payersLoadFailed = ref(false);
const payerSelectRef = ref<{
  $el: HTMLElement;
  updateInputValue(value: string, noFilter?: boolean): void;
} | null>(null);

const form = reactive<InsuranceVerificationRequest>({
  subscriberFirstName: '',
  subscriberLastName: '',
  subscriberDob: '',
  memberId: '',
  payerId: '',
  groupNumber: '',
  relationshipCode: '18',
  patientAddressLine1: '',
  patientAddressLine2: '',
  patientCity: '',
});

// Best-effort mitigation of Chrome autofilling the payer field with a saved company (e.g. "HP
// Administrative Services LLC") — the "Insurance Company" label makes Chrome classify it as an
// organization field and fill it without a real selection (payerId stays empty, Verify disabled).
// A readonly input isn't autofilled, so the field is readonly whenever the user is not engaging
// it (armed on mount and re-armed on every blur, not just until the first focus), which stops the
// fill ON OPEN. The shield is stripped on pointer/touch as well as focus: those fire BEFORE the
// focus/keyboard decision, so a still-readonly input would suppress the mobile soft keyboard.
// Chrome can still autofill in the instant the field IS engaged despite autocomplete overrides;
// that residual is display-only and is scrubbed when the dropdown closes (see onPayerPopupHide).
async function disablePayerAutofill(): Promise<void> {
  await nextTick();
  const input = payerSelectRef.value?.$el?.querySelector('input');
  // The dataset flag keeps the mount + every-dialog-open call sites from stacking listener pairs.
  if (!input || input.dataset.autofillShield === 'armed') {
    return;
  }
  input.dataset.autofillShield = 'armed';
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('name', 'payer-search');
  if (!form.payerId) {
    input.value = '';
  }
  input.setAttribute('readonly', 'readonly');
  const disarm = (): void => {
    input.removeAttribute('readonly');
  };
  input.addEventListener('mousedown', disarm);
  input.addEventListener('touchstart', disarm, {passive: true});
  input.addEventListener('focus', disarm);
  input.addEventListener('blur', () => {
    input.setAttribute('readonly', 'readonly');
  });
}

const isDependent = computed(() => form.relationshipCode !== '18');

// Result branches (final model). Access is granted for VALID + VERIFIED insurance; whether the
// requested service is covered / the payer is billable only changes the message, not the grant.
// - Unverified: the plan isn't active, or the payer can't be checked in real time → no grant.
// - Happy: the plan is active AND the requested service is covered AND the payer is billable →
//   grant without consent.
// - Otherwise (valid but not covering, or not billable yet) → ask consent to keep the data.
const isUnverified = computed(() =>
  result.value?.notVerifiable === true || result.value?.coverageActive !== true);
// Within the unverified bucket, separate "insurer can't be checked online" (410) from "plan looks
// inactive" so each shows its own reason. Same two actions in both (retry / continue without).
const isNotVerifiable = computed(() => result.value?.notVerifiable === true);
// coverageActive (the plan is active) is required as well as requestedCoverageActive (this service
// is covered): the backend derives the two from independent parts of the 271, and only persists a
// verificationId when coverageActive is true. Without this guard a response with the service
// covered but the plan inactive would show the "Covered!" screen and then dead-end on Accept
// (no verificationId) — it must fall to the unverified branch instead.
const isHappy = computed(() =>
  result.value?.coverageActive === true &&
  result.value?.requestedCoverageActive === true &&
  result.value?.payerSupported !== false);
const needsConsent = computed(() => !isUnverified.value && !isHappy.value);

// Cap for the native date inputs (ISO strings compare lexicographically): the backend requires
// strictly-past dates of birth (@Past), so the max is yesterday, computed in LOCAL time — a UTC
// cap would block "yesterday" for users east of UTC right after their local midnight.
const maxDob = ((): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
})();

// Per-field validity: drives both the touched-field error hints (WCAG 3.3.1) and, derived below,
// the submit gate — one source of truth. Dependent fields count as valid when the subscriber is
// the patient (relationship = Self, they are neither shown nor sent); when the user IS a dependent
// their identity is required even though the backend treats it as optional, because an incomplete
// dependent verification reaches the vendor (billed per transaction) and comes back as a confusing
// "no coverage found". Address is street + city only: state + ZIP are reused server-side from the
// CDC enrollment answers.
const touched = reactive<Record<string, boolean>>({});
function touch(field: string): void {
  touched[field] = true;
}
const fieldValid = computed<Record<string, boolean>>(() => ({
  subscriberFirstName: form.subscriberFirstName.trim() !== '',
  subscriberLastName: form.subscriberLastName.trim() !== '',
  subscriberDob: form.subscriberDob !== '' && form.subscriberDob <= maxDob,
  memberId: form.memberId.trim() !== '',
  payerId: form.payerId !== '',
  dependentFirstName: !isDependent.value || (form.dependentFirstName ?? '').trim() !== '',
  dependentLastName: !isDependent.value || (form.dependentLastName ?? '').trim() !== '',
  dependentDob: !isDependent.value
    || ((form.dependentDob ?? '') !== '' && (form.dependentDob ?? '') <= maxDob),
  patientAddressLine1: (form.patientAddressLine1 ?? '').trim() !== '',
  patientCity: (form.patientCity ?? '').trim() !== '',
}));
function fieldError(field: string): boolean {
  return touched[field] && !fieldValid.value[field];
}

// Closing the dropdown is the payer field's "visited" moment (fires after a selection commits or
// when the list is abandoned, so the required hint cannot flash mid-selection). It also scrubs
// anything Chrome's autofill wrote into the search input while it was focused (see
// disablePayerAutofill): without a real selection that text would keep sitting there looking like
// a chosen payer while payerId stays empty. Restoring the input to the actual selection (or to
// empty, bringing the placeholder back) makes the visible text and the model agree again.
function onPayerPopupHide(): void {
  touch('payerId');
  const selectedName = payers.value.find((payer) => payer.id === form.payerId)?.name ?? '';
  payerSelectRef.value?.updateInputValue(selectedName, true);
}

const canSubmit = computed(() =>
  Object.values(fieldValid.value).every(Boolean)
  && state.value === 'FORM');

// Announces phase changes to assistive tech (the v-if swaps are silent otherwise, WCAG 4.1.3) and
// moves focus to the result title so a screen-reader user is not left on a removed button.
const statusAnnouncement = computed((): string => {
  if (state.value === 'VERIFYING') {return t('insurance_verifying');}
  if (state.value === 'ACCEPTING') {return t('insurance_accepting');}
  if (state.value === 'ERROR') {return errorTitle.value;}
  if (state.value === 'SUCCESS') {
    if (isHappy.value) {return t('insurance_covered_title');}
    if (needsConsent.value) {return t('insurance_consent_title');}
    return isNotVerifiable.value ? t('insurance_not_verifiable_title') : t('insurance_plan_inactive_title');
  }
  return '';
});

const resultTitleRef = ref<HTMLElement | null>(null);
watch(state, async(next) => {
  if (next === 'SUCCESS' || next === 'ERROR') {
    await nextTick();
    resultTitleRef.value?.focus();
  }
});

async function loadPayers(): Promise<void> {
  isLoadingPayers.value = true;
  payersLoadFailed.value = false;
  try {
    const directory = await loadCachedPayerDirectory(async() => api.me.getInsurancePayers());
    // The vendor directory contains entries with a BLANK payer id (Stedi serves primaryPayerId: ""
    // for some payers and the backend maps id = primaryPayerId ?: stediId, which an empty string
    // does not trigger). A blank id equals the pristine form's payerId, so q-select's map-options
    // would render such a payer as the current selection of an untouched field — a phantom that
    // every browser shows and the popup-hide scrub would faithfully keep restoring. They cannot be
    // verified anyway (selecting one leaves payerId empty and Verify disabled), so drop them.
    payers.value = directory.filter((payer) => payer.id.trim() !== '');
    payerOptions.value = payers.value;
  } catch (error) {
    // Without feedback the user faces an empty, never-populating dropdown.
    payersLoadFailed.value = true;
    logger.error('Failed to load payers: {}', error)();
  } finally {
    isLoadingPayers.value = false;
  }
}

watch(showModal, async(visible) => {
  if (visible) {
    state.value = 'FORM';
    result.value = null;
    errorMessage.value = null;
    errorStage.value = 'verify';
    await disablePayerAutofill();
    await loadPayers();
  }
});

onMounted(async() => {
  if (showModal.value && payers.value.length === 0) {
    await loadPayers();
  }
  await disablePayerAutofill();
});

// Lowercased "name id" haystack per payer, computed once when the directory loads rather than
// re-lowercasing every entry on every keystroke.
const payerSearchIndex = computed(() =>
  payers.value.map((p) => ({payer: p, haystack: `${p.name} ${p.id}`.toLowerCase()})));

// The payer menu grows to whatever fits between the field and the bottom of the viewport (the
// directory has thousands of entries, so height is scanability). Computed per opening — a static
// max-height taller than the space below would make Quasar flip the menu ABOVE the field and
// unfold it from the top of the screen. The 220px floor (the height the flipped menu gets when
// the field sits near the viewport bottom) matches the other balm-skinned menus. Delivered through a
// CSS variable read by the .payer-select-menu class: the menu is portaled to <body> and QMenu's
// position engine rewrites the element's style attribute, so neither scoped styles nor the
// popup-content-style prop survive on it.
function sizePayerMenu(): void {
  const rect = payerSelectRef.value?.$el.getBoundingClientRect();
  if (!rect) {
    return;
  }
  // visualViewport excludes the mobile soft keyboard, which window.innerHeight would count as
  // available space and hide part of the list under the keys.
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const below = Math.floor(viewportHeight - rect.bottom) - 12;
  document.documentElement.style.setProperty('--payer-menu-max-height', `${Math.max(below, 220)}px`);
}

// Quasar runs the filter on every popup opening (and on typing, when re-measuring is a no-op:
// the field does not move while the list is open), so this is the one hook that sizes the menu
// before it is positioned.
function filterPayers(val: string, update: (fn: () => void) => void): void {
  sizePayerMenu();
  update(() => {
    const needle = (val ?? '').toLowerCase();
    payerOptions.value = payerSearchIndex.value
      .filter((entry) => entry.haystack.includes(needle))
      .map((entry) => entry.payer);
  });
}

/**
 * Resolve a *verify*-step error into title + body + severity. Errors arrive as thrown
 * {@link AppError}s (FetchWrapper throws on non-2xx, carrying the backend errorCode as statusCode).
 * User-fixable results (coverage not found, invalid member id) get the calm info treatment; vendor
 * down / timeout / circuit / anything unexpected is a genuine technical (red) error.
 */
function applyVerifyError(error: unknown): void {
  const code = error instanceof AppError ? error.statusCode : null;
  const vendorDown: string[] = [
    ErrorCodesDto.VENDOR_UNAVAILABLE,
    ErrorCodesDto.VENDOR_TIMEOUT,
  ];
  if (code === ErrorCodesDto.COVERAGE_NOT_FOUND) {
    errorTitle.value = t('insurance_error_not_found_title');
    errorMessage.value = t('insurance_error_not_found_body');
    errorTechnical.value = false;
  } else if (code === ErrorCodesDto.INVALID_MEMBER_ID) {
    errorTitle.value = t('insurance_error_invalid_member_title');
    errorMessage.value = t('insurance_error_invalid_member_body');
    errorTechnical.value = false;
  } else if (code !== null && vendorDown.includes(code)) {
    errorTitle.value = t('insurance_error_title');
    errorMessage.value = t('insurance_error_vendor_body');
    errorTechnical.value = true;
  } else {
    errorTitle.value = t('insurance_error_title');
    errorMessage.value = t('insurance_verification_error_generic');
    errorTechnical.value = true;
  }
}

// The accept step runs after a successful verify, so its failures are a problem setting up the
// funded access. A STALE verification (coverage no longer active, or the verification deleted)
// can never be accepted again — those are classified as verify-stage errors so the existing Retry
// returns to the form to re-verify instead of re-attempting a dead accept. Everything else stays
// technical (red).
function applyAcceptError(error?: unknown): void {
  const code = error instanceof AppError ? error.statusCode : null;
  if (code === ErrorCodesDto.COVERAGE_NOT_ACTIVE || code === ErrorCodesDto.VERIFICATION_NOT_FOUND) {
    errorStage.value = 'verify';
    errorTitle.value = t('insurance_error_title');
    errorMessage.value = t('insurance_accept_stale_error');
    errorTechnical.value = false;
    return;
  }
  errorStage.value = 'accept';
  errorTitle.value = t('insurance_error_title');
  errorMessage.value = t('insurance_accept_error');
  errorTechnical.value = true;
}

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) {
    return;
  }

  state.value = 'VERIFYING';
  errorMessage.value = null;

  try {
    // Built explicitly (no form spread): stale dependent values from a previous relationship
    // selection must not travel once the user switches back to Self.
    const request: InsuranceVerificationRequest = {
      subscriberFirstName: form.subscriberFirstName.trim(),
      subscriberLastName: form.subscriberLastName.trim(),
      subscriberDob: form.subscriberDob,
      memberId: form.memberId.trim().replaceAll(/[\s-]/gu, ''),
      payerId: form.payerId,
      relationshipCode: form.relationshipCode,
      groupNumber: form.groupNumber?.trim() || undefined, // eslint-disable-line no-undefined
      patientAddressLine1: form.patientAddressLine1?.trim(),
      patientAddressLine2: form.patientAddressLine2?.trim() || undefined, // eslint-disable-line no-undefined
      patientCity: form.patientCity?.trim(),
      interventionId: props.interventionId,
    };
    if (isDependent.value) {
      request.dependentFirstName = form.dependentFirstName?.trim();
      request.dependentLastName = form.dependentLastName?.trim();
      request.dependentDob = form.dependentDob;
    }

    result.value = await api.me.verifyInsurance(request);
    state.value = 'SUCCESS';
  } catch (error) {
    logger.error('Insurance verification failed: {}', error)();
    errorStage.value = 'verify';
    applyVerifyError(error);
    state.value = 'ERROR';
  }
}

function handleSkip(): void {
  showModal.value = false;
  emit('skipped');
}

async function handleAccept(): Promise<void> {
  const verificationId = result.value?.verificationId;
  if (!verificationId) {
    applyAcceptError();
    state.value = 'ERROR';
    return;
  }

  state.value = 'ACCEPTING';
  errorMessage.value = null;

  try {
    await api.me.acceptInsurance({insuranceVerificationId: verificationId});
    showModal.value = false;
    if (result.value) {
      emit('funded', result.value);
    }
  } catch (error) {
    logger.error('Insurance acceptance failed: {}', error)();
    applyAcceptError(error);
    state.value = 'ERROR';
  }
}

function handleRetry(): void {
  errorMessage.value = null;
  // An accept that failed after a successful verify keeps a valid verificationId — retry just the
  // accept rather than sending the user back through the form (a second billable verify). A verify
  // failure, or an accept with no verificationId, still returns to the form to re-verify.
  if (errorStage.value === 'accept' && result.value?.verificationId) {
    void handleAccept();
    return;
  }
  state.value = 'FORM';
}

// Consent declined: the user doesn't want us to keep their insurance data. Delete the stored
// verification (best-effort — failing to delete must not block them) and continue on current access.
async function handleDeclineConsent(): Promise<void> {
  const verificationId = result.value?.verificationId;
  if (verificationId) {
    try {
      await api.me.deleteInsuranceVerification(verificationId);
    } catch (error) {
      logger.error('Failed to delete insurance verification: {}', error)();
      $q.notify({type: 'negative', message: t('insurance_delete_error')});
    }
  }
  handleSkip();
}

// The header X goes back to the eligibility decision modal (check insurance / continue without /
// exit) rather than silently closing — the form is a sub-step of that choice, not a dead end.
function handleBack(): void {
  showModal.value = false;
  emit('back');
}
</script>

<template>
  <balm-dialog
    v-model="showModal"
    theme="neutral-0"
    type="fit-content"
    desktop-from-tablet
    :no-x="true"
    :no-scroll="true"
    persistent
    role="dialog"
    :aria-label="$t('insurance_verification_title')"
    aria-modal="true"
  >
    <div class="insurance-dialog">
      <!-- Announces phase changes (verifying / result / error) to assistive tech; visually hidden. -->
      <span class="sr-only" role="status" aria-live="polite">{{ statusAnnouncement }}</span>
      <!-- Header (form title + back-to-options X) only while filling the form. In result/loading
           states each variant shows its own centered title, so the generic "Insurance Information"
           header would be redundant + off-centre, and a close there is ambiguous (could keep a
           persisted verification without consent). -->
      <header v-if="state === 'FORM'" class="dialog-header">
        <h2 class="dialog-title">
          {{ $t('insurance_verification_title') }}
        </h2>
        <flat-icon-btn
          class="dismiss-btn"
          icon="x-close.svg"
          :label="$t('insurance_back_aria')"
          @click="handleBack"
        />
      </header>

      <!-- FORM STATE -->
      <main v-if="state === 'FORM'" class="dialog-content">
        <p class="dialog-description">
          {{ $t('insurance_verification_description') }}
        </p>

        <div class="form-grid">
          <div class="form-field form-field--full">
            <label class="field-label" for="insurance-first-name">{{ $t('insurance_first_name') }}</label>
            <!-- maxlength values mirror the backend's InsuranceVerificationInput @Size limits -->
            <q-input
              v-model="form.subscriberFirstName"
              for="insurance-first-name"
              dense
              outlined
              maxlength="60"
              aria-required="true"
              hide-bottom-space
              :error="fieldError('subscriberFirstName')"
              :error-message="$t('insurance_field_required')"
              @blur="touch('subscriberFirstName')"
            />
          </div>

          <div class="form-field form-field--full">
            <label class="field-label" for="insurance-last-name">{{ $t('insurance_last_name') }}</label>
            <q-input
              v-model="form.subscriberLastName"
              for="insurance-last-name"
              dense
              outlined
              maxlength="60"
              aria-required="true"
              hide-bottom-space
              :error="fieldError('subscriberLastName')"
              :error-message="$t('insurance_field_required')"
              @blur="touch('subscriberLastName')"
            />
          </div>

          <div class="form-field">
            <label class="field-label" for="insurance-dob">{{ $t('insurance_dob') }}</label>
            <q-input
              v-model="form.subscriberDob"
              for="insurance-dob"
              dense
              outlined
              type="date"
              :max="maxDob"
              aria-required="true"
              hide-bottom-space
              :error="fieldError('subscriberDob')"
              :error-message="$t('insurance_dob_invalid')"
              @blur="touch('subscriberDob')"
            />
          </div>

          <div class="form-field">
            <label class="field-label" for="insurance-member-id">{{ $t('insurance_member_id') }}</label>
            <q-input
              v-model="form.memberId"
              for="insurance-member-id"
              dense
              outlined
              maxlength="80"
              aria-required="true"
              hide-bottom-space
              :error="fieldError('memberId')"
              :error-message="$t('insurance_field_required')"
              :placeholder="$t('insurance_member_id_placeholder')"
              @blur="touch('memberId')"
            />
            <span class="field-hint">{{ $t('insurance_member_id_hint') }}</span>
          </div>

          <div class="form-field form-field--full">
            <label class="field-label" for="insurance-payer">{{ $t('insurance_payer') }}</label>
            <q-select
              ref="payerSelectRef"
              v-model="form.payerId"
              for="insurance-payer"
              dense
              outlined
              use-input
              fill-input
              hide-selected
              behavior="menu"
              input-debounce="250"
              emit-value
              map-options
              option-value="id"
              option-label="name"
              :options="payerOptions"
              :loading="isLoadingPayers"
              :placeholder="$t('insurance_payer_placeholder')"
              popup-content-class="payer-select-menu"
              hide-bottom-space
              aria-required="true"
              :error="fieldError('payerId')"
              :error-message="$t('insurance_field_required')"
              @popup-hide="onPayerPopupHide"
              @filter="filterPayers"
            />
            <span v-if="payersLoadFailed" class="field-error">
              {{ $t('insurance_payers_load_error') }}
            </span>
          </div>

          <div class="form-field">
            <label class="field-label" for="insurance-group-number">{{ $t('insurance_group_number') }}</label>
            <q-input
              v-model="form.groupNumber"
              for="insurance-group-number"
              dense
              outlined
              maxlength="80"
              :placeholder="$t('insurance_group_number_placeholder')"
            />
            <span class="field-hint">{{ $t('insurance_group_number_hint') }}</span>
          </div>

          <div class="form-field">
            <label class="field-label" for="insurance-relationship">{{ $t('insurance_relationship') }}</label>
            <q-select
              v-model="form.relationshipCode"
              for="insurance-relationship"
              dense
              outlined
              emit-value
              map-options
              :options="[
                {value: '18', label: $t('insurance_rel_self')},
                {value: '01', label: $t('insurance_rel_spouse')},
                {value: '19', label: $t('insurance_rel_child')},
                {value: '34', label: $t('insurance_rel_other')},
              ]"
            />
          </div>

          <template v-if="isDependent">
            <div class="form-field form-field--full">
              <label class="field-label" for="insurance-dependent-first-name">{{ $t('insurance_dependent_first_name') }}</label>
              <q-input
                v-model="form.dependentFirstName"
                for="insurance-dependent-first-name"
                dense
                outlined
                maxlength="60"
                aria-required="true"
                hide-bottom-space
                :error="fieldError('dependentFirstName')"
                :error-message="$t('insurance_field_required')"
                @blur="touch('dependentFirstName')"
              />
            </div>
            <div class="form-field form-field--full">
              <label class="field-label" for="insurance-dependent-last-name">{{ $t('insurance_dependent_last_name') }}</label>
              <q-input
                v-model="form.dependentLastName"
                for="insurance-dependent-last-name"
                dense
                outlined
                maxlength="60"
                aria-required="true"
                hide-bottom-space
                :error="fieldError('dependentLastName')"
                :error-message="$t('insurance_field_required')"
                @blur="touch('dependentLastName')"
              />
            </div>
            <div class="form-field">
              <label class="field-label" for="insurance-dependent-dob">{{ $t('insurance_dependent_dob') }}</label>
              <q-input
                v-model="form.dependentDob"
                for="insurance-dependent-dob"
                dense
                outlined
                type="date"
                :max="maxDob"
                aria-required="true"
                hide-bottom-space
                :error="fieldError('dependentDob')"
                :error-message="$t('insurance_dob_invalid')"
                @blur="touch('dependentDob')"
              />
            </div>
          </template>

          <!-- Patient (the user) postal address — required for the insurance claim -->
          <div class="form-field form-field--full">
            <h4 class="form-section-title">
              {{ $t('insurance_address_section') }}
            </h4>
          </div>

          <div class="form-field form-field--full">
            <label class="field-label" for="insurance-address-1">{{ $t('insurance_address_line1') }}</label>
            <q-input
              v-model="form.patientAddressLine1"
              for="insurance-address-1"
              dense
              outlined
              maxlength="80"
              aria-required="true"
              hide-bottom-space
              :error="fieldError('patientAddressLine1')"
              :error-message="$t('insurance_field_required')"
              :placeholder="$t('insurance_address_line1_placeholder')"
              @blur="touch('patientAddressLine1')"
            />
          </div>

          <div class="form-field form-field--full">
            <label class="field-label" for="insurance-address-2">{{ $t('insurance_address_line2') }}</label>
            <q-input
              v-model="form.patientAddressLine2"
              for="insurance-address-2"
              dense
              outlined
              maxlength="80"
              :placeholder="$t('insurance_address_line2_placeholder')"
            />
          </div>

          <div class="form-field form-field--full">
            <label class="field-label" for="insurance-city">{{ $t('insurance_city') }}</label>
            <q-input
              v-model="form.patientCity"
              for="insurance-city"
              dense
              outlined
              maxlength="60"
              aria-required="true"
              hide-bottom-space
              :error="fieldError('patientCity')"
              :error-message="$t('insurance_field_required')"
              @blur="touch('patientCity')"
            />
          </div>
        </div>

        <div class="dialog-actions">
          <balm-btn
            :label="$t('insurance_verify_btn')"
            theme="blue"
            paddings="p2-5"
            :disable="!canSubmit"
            @click="handleSubmit"
          />
        </div>
      </main>

      <!-- VERIFYING STATE -->
      <main v-else-if="state === 'VERIFYING'" class="dialog-content dialog-content--center">
        <q-spinner-dots size="48px" color="primary"/>
        <p class="verifying-text">
          {{ $t('insurance_verifying') }}
        </p>
      </main>

      <!-- SUCCESS STATE — coverage verified -->
      <main v-else-if="state === 'SUCCESS'" class="dialog-content dialog-content--center">
        <!-- Happy path: requested service covered AND payer billable → fund access with insurance -->
        <template v-if="isHappy">
          <span class="tone-icon tone-icon--success">
            <q-icon name="check" size="24px"/>
          </span>
          <h3 ref="resultTitleRef" tabindex="-1" class="result-title">
            {{ $t('insurance_covered_title') }}
          </h3>
          <div v-if="result" class="result-details">
            <div v-if="result.planName" class="result-row">
              <span class="result-label">{{ $t('insurance_plan') }}</span>
              <span class="result-value">{{ result.planName }}</span>
            </div>
            <div v-if="result.groupNumber" class="result-row">
              <span class="result-label">{{ $t('insurance_group') }}</span>
              <span class="result-value">{{ result.groupNumber }}</span>
            </div>
          </div>
          <p class="result-body">
            {{ $t('insurance_covered_body') }}
          </p>
          <div class="dialog-actions">
            <balm-btn
              :label="$t('insurance_accept_btn')"
              theme="blue"
              paddings="p2-5"
              @click="handleAccept"
            />
            <!-- The user is only checking — declining must not be coerced. Treat like the consent
                 decline: delete the stored verification (don't keep PHI they opted out of) → fallback. -->
            <balm-btn
              :label="$t('insurance_consent_decline_btn')"
              theme="plain-underline"
              paddings="none"
              @click="handleDeclineConsent"
            />
          </div>
        </template>

        <!-- Valid insurance, but not (covered AND billable): ask consent to keep the data for access -->
        <template v-else-if="needsConsent">
          <span class="tone-icon tone-icon--info">
            <span class="tone-glyph"/>
          </span>
          <h3 ref="resultTitleRef" tabindex="-1" class="result-title">
            {{ $t('insurance_consent_title') }}
          </h3>
          <p class="result-body">
            {{ $t('insurance_consent_body') }}
          </p>
          <div class="dialog-actions">
            <balm-btn
              :label="$t('insurance_consent_accept_btn')"
              theme="blue"
              paddings="p2-5"
              @click="handleAccept"
            />
            <balm-btn
              :label="$t('insurance_consent_decline_btn')"
              theme="plain-underline"
              paddings="none"
              @click="handleDeclineConsent"
            />
          </div>
        </template>

        <!-- Unverified: insurer has no online verification (410), or the plan looks inactive. Access is
             unaffected; offer to retry (fix details / change insurer) or continue without insurance. -->
        <template v-else>
          <span class="tone-icon tone-icon--warning">
            <span class="tone-glyph"/>
          </span>
          <h3 ref="resultTitleRef" tabindex="-1" class="result-title">
            {{ isNotVerifiable ? $t('insurance_not_verifiable_title') : $t('insurance_plan_inactive_title') }}
          </h3>
          <p class="result-body">
            {{ isNotVerifiable ? $t('insurance_not_verifiable_body') : $t('insurance_plan_inactive_body') }}
          </p>
          <div class="dialog-actions">
            <balm-btn
              :label="$t('insurance_retry_btn')"
              theme="blue"
              paddings="p2-5"
              @click="handleRetry"
            />
            <balm-btn
              :label="$t('insurance_continue_without_btn')"
              theme="plain-underline"
              paddings="none"
              @click="handleSkip"
            />
          </div>
        </template>
      </main>

      <!-- ACCEPTING STATE — granting insurance-backed access -->
      <main v-else-if="state === 'ACCEPTING'" class="dialog-content dialog-content--center">
        <q-spinner-dots size="48px" color="primary"/>
        <p class="verifying-text">
          {{ $t('insurance_accepting') }}
        </p>
      </main>

      <!-- ERROR STATE — red for genuine technical failures; calm info for "couldn't find coverage" -->
      <main v-else-if="state === 'ERROR'" class="dialog-content dialog-content--center">
        <span class="tone-icon" :class="errorTechnical ? 'tone-icon--error' : 'tone-icon--warning'">
          <span class="tone-glyph"/>
        </span>
        <h3 ref="resultTitleRef" tabindex="-1" class="result-title">
          {{ errorTitle }}
        </h3>
        <p :class="errorTechnical ? 'error-message' : 'result-body'">
          {{ errorMessage }}
        </p>
        <div class="dialog-actions">
          <balm-btn
            :label="$t('insurance_retry_btn')"
            theme="blue"
            paddings="p2-5"
            @click="handleRetry"
          />
          <balm-btn
            :label="$t('insurance_continue_without_btn')"
            theme="plain-underline"
            @click="handleSkip"
          />
        </div>
      </main>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.insurance-dialog {
  padding: var(--Spacing-spacing-4);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  // Shrink with the modal-card when it caps below 90vh, else its overflow:hidden clips the bottom padding.
  min-height: 0;

  // Floats from tablet up (desktop-from-tablet), so size it from md too — consistent with the CDC
  // dialogs (all feature prompts share a ~500px min width; the form may grow to 600).
  @include screen-md {
    min-width: 500px;
    max-width: 600px;
  }

  // The native date input's calendar icon is a browser control that ignores our theme (renders dark
  // in dark mode). Invert it in dark mode so it stays visible — mirrors admin-download-report-dialog.
  @include dark-mode {
    --calendar-icon-filter: invert(1);
  }
}

:deep(.q-field__native)::-webkit-calendar-picker-indicator {
  filter: var(--calendar-icon-filter, none);
}

.dialog-header {
  text-align: center;
  flex-shrink: 0;
  position: relative;
  padding-right: var(--Spacing-spacing-8);
}

.dismiss-btn {
  position: absolute;
  top: calc(-1 * var(--Spacing-spacing-2));
  right: calc(-1 * var(--Spacing-spacing-2));
}

.dialog-title {
  @include font-h2-bold;
  margin-bottom: var(--Spacing-spacing-2);
}

.dialog-description {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-60);
  margin-bottom: var(--Spacing-spacing-4);
}

.dialog-content {
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  &--center {
    align-items: center;
    gap: var(--Spacing-spacing-4);
    padding: var(--Spacing-spacing-4) 0;
  }
}

.form-grid {
  display: grid;
  // Mobile-first: a single column on small screens (no cramped pairs, no cut-off hints);
  // two columns only from tablet up.
  grid-template-columns: 1fr;
  gap: var(--Spacing-spacing-3);

  @include screen-md {
    grid-template-columns: 1fr 1fr;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);

  &--full {
    grid-column: 1 / -1;
  }
}

.form-section-title {
  @include font-body-bold;

  margin: var(--Spacing-spacing-2) 0 0;
}

.field-label {
  @include font-body-sm;
  font-weight: 600;
}

.field-hint {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-50);
}

.field-error {
  @include font-body-sm;
  color: var(--Functional-negative);
}

.dialog-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  margin-top: var(--Spacing-spacing-5);
  width: 100%;

  // Primary is a full-width solid button; the secondary (plain-underline) stays a centered link.
  :deep(.balm-btn:not(.plain-underline)) {
    width: 100%;
  }
}

// In the result/loading states the centered layout already spaces children via its gap, so the
// actions don't need the form's extra top margin (which otherwise reads as a big gap under the body).
.dialog-content--center .dialog-actions {
  margin-top: 0;
}

.tone-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  // Constant light glyph: the tone circles are saturated brand colors in BOTH themes, so the glyph
  // stays light in light and dark mode (not theme-adaptive by design).
  color: #fff;
}

.tone-icon--success {
  // Stable deep green in both themes (primary-color-2 turns pale in dark; completed-surface doesn't).
  background-color: var(--Functional-wl-completed-surface);
}

.tone-icon--info {
  background-color: var(--Primary-primary-color-1);
}

.tone-icon--warning {
  // Stable amber in both themes.
  background-color: var(--Primary-primary-color-4);
}

.tone-icon--error {
  background-color: var(--Functional-wl-error-surface);
}

.tone-glyph {
  @include font-body-bold;
  font-size: 24px;
  line-height: 1;
}

// Glyph provided via CSS (not template text) so it isn't flagged as untranslated copy.
.tone-icon--info .tone-glyph::before {
  content: 'i';
}

.tone-icon--warning .tone-glyph::before,
.tone-icon--error .tone-glyph::before {
  content: '!';
}

.verifying-text {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-60);
}

.result-title {
  @include font-display-4;
  text-align: center;
}

.result-body {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-60);
  max-width: 400px;
  text-align: center;
  margin: 0;
}

.result-details {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
  padding: var(--Spacing-spacing-3);
  border-radius: var(--Radius-radius-2);
  background: var(--Neutral-UI-neutral-ui-5);
}

.result-row {
  display: flex;
  justify-content: space-between;
}

.result-label {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-60);
}

.result-value {
  @include font-body-sm;
  font-weight: 600;
}

.error-message {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-60);
  max-width: 400px;
  text-align: center;
  margin: 0;
}
</style>

<style lang="scss">
// The options popup renders in a body-level portal, so this cannot be scoped. The class is set via
// popup-content-class and only this selector uses it. The variable is stamped per opening by
// sizePayerMenu (space below the field); the 220px fallback covers the first paint. Without a cap,
// the multi-thousand-payer directory makes the menu claim the tallest available space — when that
// is ABOVE the field (window geometry dependent), Quasar flips it upward and it unfolds from the
// top of the screen.
.q-menu.payer-select-menu {
  max-height: var(--payer-menu-max-height, 220px);
}
</style>
